import { ComponentResource } from '@/api/serverApi'
import humanProductType from '@/helpers/productType'
import { AppDispatch, RootState, store } from '@/stores'
import { deleteComponentAction, deselectComponent, setSelectedComponent } from '@/stores/active-design'
import { PropertyTabs, setCurrentPropertiesTab } from '@/stores/selected-properties-tab'
import { BranchIcon, ChevronIcon, DownCaret, Dropdown, LoadingSpinner } from '@cfs-fixings/shared'
import { Disclosure } from '@headlessui/react'
import { createSelector } from '@reduxjs/toolkit'
import classNames from 'classnames'
import { ReactNode, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AddProductPicker from './product-picker/AddProductPicker'
import ReplaceProductPicker from './product-picker/ReplaceProductPicker'
import ComponentType from '@/enums/ComponentType'

type ComponentTree = ComponentResource & { children: ComponentTree[] }

type LeafDropdownPropTypes = {
  component: ComponentTree
  parent?: ComponentTree
  onProductAdd?: (productType?: string, parentId?: string) => void
  onProductReplace?: (productType?: string, parentId?: string, componentId?: string) => void
}

type ExplorerLeafPropTypes = {
  component: ComponentTree
  parent?: ComponentTree
  indent?: number
  isLastElement?: boolean
  onProductAdd?: (productType?: string, parentId?: string) => void
  onProductReplace?: (productType?: string, parentId?: string, componentId?: string) => void
}

const productsSelector = createSelector(
  [
    (state: RootState) => state.products.products,
    (state: RootState) => state.products.productVariants,
  ],
  (products, productVariants) => ({
    products, productVariants,
  }),
)

const createGroupObject = (groupId: string, parentId: string): ComponentResource => {
  return {
    id: groupId,
    parent_id: parentId,
    type: ComponentType.Group,
    product_variant_id: '',
    position: {
      x: 0,
      y: 0,
      z: 0,
    },
    group_id: '',
    model: ComponentType.Group,
  }
}

const LeafDropdown = ({ onProductAdd, onProductReplace, component, parent }: LeafDropdownPropTypes) => {
  const dispatch = useDispatch<AppDispatch>()
  const { products, productVariants } = productsSelector(store.getState())

  const variant = productVariants.find(pv => pv.id === component.product_variant_id)
  const product = products.find(p => p.id === variant?.product_id)

  const options: ReactNode[] = []

  if (component.type === 'group') {
    return null
  }

  if (component.type === 'concrete_slab' && component.children.length === 0) {
    options.push(
      <Dropdown.Item
        key="add-channel"
        onClick={() =>  {
          onProductAdd?.('cast_in_anchor_channel', component.id)
        }}
      >
        Add Channel
      </Dropdown.Item>,
    )
  }

  if (product?.type === 'cast_in_anchor_channel') {
    options.push(
      <Dropdown.Item
        key="add-tbolt"
        onClick={() => {
          onProductAdd?.('t_bolt', component.id)
        }}
      >
        Add T-bolt
      </Dropdown.Item>,
    )
    options.push(
      <Dropdown.Item
        key="configure-cast-in-channel"
        onClick={() => {
          onProductReplace?.('cast_in_anchor_channel', parent?.id, component.id)
        }}
      >
        Configure
      </Dropdown.Item>,
    )
  }

  if (product?.type === 't_bolt') {
    let parentId = parent?.id

    /**
     * Since a parent object is made manually to group children, we need to find the actual parent
     * which can be found on the parent_id of the component
     */
    if (parent?.type === ComponentType.Group) {
      if (component.parent_id) {
        parentId = component.parent_id
      }
    }

    options.push(
      <Dropdown.Item
        key="configure-tbolt"
        onClick={() => {
          onProductReplace?.('t_bolt', parentId, component.id)
        }}
      >
        Configure
      </Dropdown.Item>,
    )
  }

  if (component.type !== 'concrete_slab') {
    if (options.length > 0) {
      options.push(<Dropdown.Separator key="separator" />)
    }
    options.push(
      <Dropdown.Item
        key="remove"
        onClick={() =>  {
          dispatch(deleteComponentAction(component.id))
        }}
      >
        Remove
      </Dropdown.Item>,
    )
  }

  if (options.length === 0) {
    return null
  }

  return (
    <Dropdown>
      {options}
    </Dropdown>
  )
}

const ExplorerLeaf = ({ onProductAdd, onProductReplace, component, parent, indent = 1, isLastElement = false }: ExplorerLeafPropTypes) => {
  const dispatch = useDispatch()
  const { products, productVariants } = productsSelector(store.getState())
  const selectedComponentId = useSelector((state: RootState) => state.activeDesign.selectedComponentId)

  const variant = productVariants.find(pv => pv.id === component.product_variant_id)
  const product = products.find(p => p.id === variant?.product_id)

  const isComponentGroup = component?.type === ComponentType.Group

  const componentName = () => {
    if (component.type === ComponentType.ConcreteSlab) {
      return `${humanProductType(component?.type ?? '')} - ${component.concrete_slab?.strength}`
    }

    if (component.type === ComponentType.Group) {
      return 'Group'
    }

    return `${humanProductType(product?.type ?? '')} - ${variant?.name}`
  }

  const componentClicked = (id: string) => {
    if (selectedComponentId === id) {
      dispatch(deselectComponent())
      return dispatch(setCurrentPropertiesTab(PropertyTabs.Results))
    }

    if (isComponentGroup) {
      dispatch(deselectComponent())
      return dispatch(setCurrentPropertiesTab(PropertyTabs.Loads))
    }

    dispatch(setSelectedComponent(id))
    return dispatch(setCurrentPropertiesTab(PropertyTabs.Selected))
  }

  // TODO: Figure out the math to make this work at scale
  const paddingOffset = () => {
    // No indent for the first level
    let offset = 0
    if (indent >= 2) {
      offset += 8
    }
    if (indent >= 3) {
      offset += 14.5
    }
    if (indent >= 4) {
      offset += 14.5
    }
    return offset
  }

  return (
    <Disclosure defaultOpen>
      {({ open }) => (
        <>
          <div
            className={classNames(
              'flex items-center w-[calc(100%-14px)] hover:bg-gray-50 rounded-[4px] text-gray-900 min-h-0 h-[26px]',
              {
                'bg-primary-500 hover:bg-primary-100': selectedComponentId === component.id,
              },
            )}
            style={{
              paddingLeft: `${paddingOffset()}px`,
            }}
          >
            {
              parent
                && (
                  <div className="grid grid-flow-col grid-rows-2 min-h-0 h-[26px] w-[8px]">
                    <div className="border-l border-b h-full border-gray-400" />
                    <div className={`h-full border-gray-400 ${isLastElement ? '' : 'border-l'}`} />
                  </div>
                )
            }
            <Disclosure.Button>
              {
                component.children && component.children.length > 0 && (
                  <DownCaret className={classNames(
                    'w-[16px] h-[16px] transition',
                    {
                      'rotate-0': open,
                      'rotate-180': !open,
                    },
                  )}
                  />
                )
              }
            </Disclosure.Button>
            <div className="flex items-center justify-between w-full pr-1">
              <button
                type="button"
                className="truncate text-left w-[85%] text-[12px]"
                onClick={() => componentClicked(component.id)}
              >
                {componentName()}
              </button>

              <LeafDropdown
                component={component}
                parent={parent}
                onProductAdd={onProductAdd}
                onProductReplace={onProductReplace}
              />
            </div>
          </div>
          <Disclosure.Panel>
            {
              component.children.map((c, i) => (
                <ExplorerLeaf
                  key={c.id}
                  component={c}
                  parent={component}
                  indent={indent + 1}
                  isLastElement={i === component.children.length - 1}
                  onProductAdd={onProductAdd}
                  onProductReplace={onProductReplace}
                />
              ))
            }
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  )
}

const ObjectExplorer = () => {
  const design = useSelector((state: RootState) => state.activeDesign.design)
  const isReplacing = useSelector((state: RootState) => state.activeDesign.replacing)

  const [isAddProductPickerOpen, setIsAddProductPickerOpen] = useState<boolean>(false)
  const [isReplaceProductPickerOpen, setIsReplaceProductPickerOpen] = useState<boolean>(false)

  const [productPickerType, setProductPickerType] = useState<string | undefined>()
  const [parentComponentId, setParentComponentId] = useState<string | undefined>()
  const [selectedComponentId, setSelectedComponentId] = useState<string | undefined>()

  const tree = useMemo(() => {
    if (!design) {
      return []
    }

    const flatParent = design.components.filter(com => !com.parent_id && !com.group_id)
    const flatChildren = design.components.filter(com => com.parent_id || com.group_id).sort((a, b) => {
      const hasAParent = a.parent_id || a.group_id
      const hasBParent = b.parent_id || b.group_id
      if (hasAParent && !hasBParent) {
        return 0
      }

      return 1
    })

    const branchContainsGroups = flatChildren.some(com => com.group_id)
    if (branchContainsGroups) {
      const uniqueGroupIds = Array.from(new Set(flatChildren.map(com => com.group_id).filter(s => s)))

      // Create a group object for group children to be linked to
      uniqueGroupIds.forEach(groupId => {
        const parentId = flatChildren.find((com) => com.group_id === groupId)?.parent_id
        const groupObject = createGroupObject(groupId, parentId!)
        flatChildren.push(groupObject)
      })
    }

    /**
     * If children have a group ID they should only be shown under the group, not under their actual parent.
     * This allows us to add a virtual group of T-bolts under a channel without changing anything about the T-bolts, including the parent ID
     */
    const getChildren = (node: ComponentResource): ComponentTree[] => {
      const children = flatChildren.filter(child => {
        const isUnGroupedChild = child.parent_id === node.id && !child.group_id
        const isGroupedChild = child.group_id === node.id

        return isUnGroupedChild || isGroupedChild
      })

      return children.map(child => ({
        ...child,
        children: getChildren(child),
      }))
    }

    return flatParent.map(child => ({
      ...child,
      children: getChildren(child),
    }))
  }, [design])


  return (
    <>
      {
        isReplacing
          && (
            <div className="h-full flex items-center justify-center">
              <LoadingSpinner className="h-4 w-4" />
            </div>
          )
      }
      {
        !isReplacing
          && (
            <Disclosure defaultOpen>
              {({ open }) => (
                <>
                  <Disclosure.Button className="pl-[14px] pr-[12px] py-[20px] uppercase font-mono flex items-center w-full text-gray-900">
                    <BranchIcon className="w-[14px] h-[10px]" />
                    <span className="ml-[5px] font-semibold text-[13px]">Object Explorer</span>
                    <ChevronIcon className={classNames(
                      'w-[10px] h-[6px] ml-auto transition',
                      {
                        'rotate-0': open,
                        'rotate-180': !open,
                      },
                    )}
                    />
                  </Disclosure.Button>

                  <Disclosure.Panel className="pl-[10px]">
                    {
                      tree.map(c => (
                        <ExplorerLeaf
                          key={c.id}
                          component={c}
                          onProductAdd={(productType, parentId) => {
                            setParentComponentId(parentId)
                            setProductPickerType(productType)
                            setIsAddProductPickerOpen(true)
                          }}
                          onProductReplace={(productType, parentId, componentId) => {
                            setParentComponentId(parentId)
                            setSelectedComponentId(componentId)
                            setProductPickerType(productType)
                            setIsReplaceProductPickerOpen(true)
                          }}
                        />
                      ))
                    }
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          )
      }
      <AddProductPicker
        productType={productPickerType}
        parentId={parentComponentId}
        isOpen={isAddProductPickerOpen}
        onClose={() => setIsAddProductPickerOpen(false)}
      />
      <ReplaceProductPicker
        productType={productPickerType}
        parentId={parentComponentId}
        componentId={selectedComponentId}
        isOpen={isReplaceProductPickerOpen}
        onClose={() => setIsReplaceProductPickerOpen(false)}
      />
    </>
  )
}

export default ObjectExplorer
